import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useNotificationPermission } from '@/hooks/useNotificationPermission';
import { usePWA } from '@/hooks/usePWA';
import { PushService, UsuariosService } from '@/services';
import { Usuario } from '@/types';
import { 
  User, 
  Mail, 
  Camera, 
  Save, 
  X, 
  ArrowLeft,
  Building2,
  MapPin,
  Eye,
  EyeOff,
  Upload,
  Trash2,
  Bell,
  AlertTriangle,
  CheckCircle,
  TestTube,
  Settings,
  RefreshCw,
  Database,
  Check,
  XCircle
} from 'lucide-react';
import toast from 'react-hot-toast';
import Modal from '@/components/ui/Modal';

// Função helper para validar requisitos de senha
const validatePasswordRequirements = (password: string) => {
  return {
    minLength: password.length >= 8,
    maxLength: password.length <= 128,
    hasLowercase: /[a-z]/.test(password),
    hasUppercase: /[A-Z]/.test(password),
    hasNumber: /[0-9]/.test(password),
    hasSpecial: /[^a-zA-Z0-9]/.test(password)
  };
};

export default function UserProfile() {
  const { usuario, updateUsuario } = useAuth();
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  // Push notification hooks
  const { 
    permission, 
    isSupported, 
    hasActiveSubscription, 
    needsAttention 
  } = useNotificationPermission();
  
  const { 
    sendNotification, 
    registerPushSubscription 
  } = usePWA();
  
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isUploadingAvatar, setIsUploadingAvatar] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isTestingNotification, setIsTestingNotification] = useState(false);
  const [isDiagnosing, setIsDiagnosing] = useState(false);
  const [isClearingCache, setIsClearingCache] = useState(false);
  const [showClearCacheModal, setShowClearCacheModal] = useState(false);
  
  const [formData, setFormData] = useState({
    nome: '',
    email: '',
    password: '',
    confirmPassword: '',
    exibir_fechados: 0
  });

  const [avatarFile, setAvatarFile] = useState<File | null>(null);
  const [avatarPreview, setAvatarPreview] = useState<string>('');

  useEffect(() => {
    if (usuario) {
      setFormData({
        nome: usuario.nome || '',
        email: usuario.email || '',
        password: '',
        confirmPassword: '',
        exibir_fechados: usuario.exibir_fechados || 0
      });
      
      // Limpar o preview quando o usuário for atualizado
      if (avatarPreview && usuario.avatar) {
        setAvatarPreview('');
      }
    }
  }, [usuario]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked ? 1 : 0 : value
    }));
  };

  const handleAvatarChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validar tipo de arquivo
      const allowedTypes = ['image/jpeg', 'image/png', 'image/webp'];
      if (!allowedTypes.includes(file.type)) {
        toast.error('Tipo de arquivo não permitido. Use JPG, PNG ou WebP');
        return;
      }

      // Validar tamanho (5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Arquivo muito grande. Máximo 5MB');
        return;
      }

      setAvatarFile(file);
      
      // Criar preview
      const reader = new FileReader();
      reader.onload = (e) => {
        setAvatarPreview(e.target?.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleAvatarUpload = async () => {
    if (!avatarFile) return;

    try {
      setIsUploadingAvatar(true);
      const formData = new FormData();
      formData.append('avatar', avatarFile);

      const response = await UsuariosService.uploadAvatar(formData);
      
      if (response.success) {
        // Atualizar o usuário no contexto
        if (usuario) {
          const updatedUser = { ...usuario, avatar: response.avatar };
          updateUsuario(updatedUser);
        }
        
        toast.success('Avatar atualizado com sucesso!');
        
        // Limpar o arquivo selecionado, mas manter o preview até o usuário ser atualizado
        setAvatarFile(null);
        
        // Limpar o input de arquivo
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      }
    } catch (error: any) {
      console.error('Erro ao fazer upload do avatar:', error);
      toast.error(error.message || 'Erro ao fazer upload do avatar');
    } finally {
      setIsUploadingAvatar(false);
    }
  };

  const removeAvatar = async () => {
    try {
      setIsUploadingAvatar(true);
      
      // Enviar requisição sem arquivo para remover o avatar
      const formData = new FormData();
      // Não adicionar arquivo, deixar vazio para o backend interpretar como remoção
      
      const response = await UsuariosService.uploadAvatar(formData);
      
      if (response.success) {
        // Atualizar o usuário no contexto
        if (usuario) {
          const updatedUser = { ...usuario, avatar: response.avatar || undefined };
          updateUsuario(updatedUser);
        }
        
        toast.success('Avatar removido com sucesso!');
      }
    } catch (error: any) {
      console.error('Erro ao remover avatar:', error);
      toast.error('Erro ao remover avatar');
    } finally {
      setIsUploadingAvatar(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validações
    if (!formData.nome.trim()) {
      toast.error('Nome é obrigatório');
      return;
    }

    if (!formData.email.trim()) {
      toast.error('Email é obrigatório');
      return;
    }

    if (formData.password && formData.password !== formData.confirmPassword) {
      toast.error('As senhas não coincidem');
      return;
    }

    // Validação de senha conforme backend (SecurityHelper::validarForcaSenha)
    if (formData.password) {
      const requirements = validatePasswordRequirements(formData.password);
      if (!requirements.minLength) {
        toast.error('Senha deve ter no mínimo 8 caracteres');
        return;
      }
      if (!requirements.maxLength) {
        toast.error('Senha deve ter no máximo 128 caracteres');
        return;
      }
      if (!requirements.hasLowercase) {
        toast.error('Senha deve conter pelo menos uma letra minúscula');
        return;
      }
      if (!requirements.hasUppercase) {
        toast.error('Senha deve conter pelo menos uma letra maiúscula');
        return;
      }
      if (!requirements.hasNumber) {
        toast.error('Senha deve conter pelo menos um número');
        return;
      }
      if (!requirements.hasSpecial) {
        toast.error('Senha deve conter pelo menos um caractere especial');
        return;
      }
    }

    try {
      setIsSaving(true);
      
      const updateData: Partial<Usuario> = {
        nome: formData.nome.trim(),
        email: formData.email.trim(),
        exibir_fechados: formData.exibir_fechados
      };

      // Só incluir senha se foi alterada
      if (formData.password) {
        (updateData as any).password = formData.password;
      }

      const response = await UsuariosService.updatePerfil(updateData);
      
      // Atualizar o usuário no contexto
      if (usuario) {
        const updatedUser = { ...usuario, ...response };
        updateUsuario(updatedUser);
      }
      
      toast.success('Perfil atualizado com sucesso!');
      
      // Limpar campos de senha
      setFormData(prev => ({
        ...prev,
        password: '',
        confirmPassword: ''
      }));
    } catch (error: any) {
      console.error('Erro ao atualizar perfil:', error);
      
      // Capturar mensagens de erro do backend
      let errorMessage = 'Erro ao atualizar perfil. Tente novamente.';
      
      if (error.response?.data?.error) {
        // Se o backend retornou uma mensagem de erro específica
        const backendError = error.response.data.error;
        if (typeof backendError === 'string') {
          errorMessage = backendError;
        } else if (Array.isArray(backendError)) {
          errorMessage = backendError.join(', ');
        }
      } else if (error.response?.data?.message) {
        // Fallback para campo 'message'
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      toast.error(errorMessage);
    } finally {
      setIsSaving(false);
    }
  };

  const getCurrentAvatar = () => {
    if (avatarPreview) return avatarPreview;
    if (usuario?.avatar) return `${import.meta.env.VITE_API_URL}/img/avatars/${usuario.avatar}`;
    return undefined;
  };

  const handleTestPushNotification = async () => {
    if (!isSupported) {
      toast.error('Notificações não são suportadas neste navegador');
      return;
    }

    if (permission !== 'granted') {
      toast.error('Permissão de notificação não concedida. Configure nas notificações.');
      return;
    }

    if (!hasActiveSubscription) {
      toast.error('Assinatura push não encontrada. Ativando...');
      try {
        await registerPushSubscription();
        toast.success('Assinatura push ativada!');
      } catch (error) {
        console.error('Erro ao ativar push subscription:', error);
        toast.error('Erro ao ativar notificações push');
        return;
      }
    }

    setIsTestingNotification(true);
    try {
      // Enviar notificação local
      await sendNotification('🔔 Teste de Notificação', {
        body: `Olá ${usuario?.nome}! Suas notificações estão funcionando perfeitamente.`,
        icon: '/icons/android/android-launchericon-192-192.png',
        badge: '/icons/android/android-launchericon-96-96.png',
        tag: 'user-profile-test',
        requireInteraction: true
      });

      // Enviar notificação via backend (opcional, para testar o sistema completo)
      try {
        const response = await PushService.testPushNotification({
          titulo: '🔔 Teste de Notificação (Backend)',
          mensagem: `${usuario?.nome}, este é um teste do sistema de notificações push.`,
          dados: {
            type: 'user-test',
            user_id: usuario?.id,
            timestamp: new Date().toISOString()
          }
        });
        
        if (response.success) {
          toast.success('Notificação de teste enviada com sucesso!');
        }
      } catch (backendError) {
        console.warn('Erro ao enviar notificação via backend:', backendError);
        // Não falha a operação se o backend falhar
      }

      toast.success('Teste de notificação executado! Verifique se recebeu a notificação.');
    } catch (error) {
      console.error('Erro ao testar notificação:', error);
      toast.error('Erro ao testar notificação: ' + (error as Error).message);
    } finally {
      setIsTestingNotification(false);
    }
  };

  const handleDiagnosticoPush = async () => {
    setIsDiagnosing(true);
    try {
      const response = await PushService.diagnosticoPushNotifications();
      
      if (response.success) {
        const diag = response.diagnostico;
        
        // Mostrar resultado do diagnóstico
        let mensagem = 'Diagnóstico Push Notifications:\n\n';
        mensagem += `VAPID Configurado: ${diag.vapid_configured ? '✅ Sim' : '❌ Não'}\n`;
        mensagem += `Tabela no BD: ${diag.database_table_exists ? '✅ Existe' : '❌ Não existe'}\n`;
        mensagem += `Sua Assinatura: ${diag.user_has_subscription ? '✅ Ativa' : '❌ Inativa'}\n`;
        mensagem += `Total Assinaturas: ${diag.total_subscriptions || 0}\n`;
        mensagem += `WebPush Library: ${diag.webpush_library_loaded ? '✅ Carregada' : '❌ Não carregada'}\n`;
        
        if (diag.push_service_config) {
          mensagem += `Configuração Service: ${diag.push_service_config.success ? '✅ OK' : '❌ Erro'}\n`;
        }
        
        alert(mensagem);
        
        if (!diag.user_has_subscription && permission === 'granted') {
          toast.error('Você tem permissão mas não há assinatura no BD. Tente re-registrar.');
        }
      }
    } catch (error) {
      console.error('Erro no diagnóstico:', error);
      toast.error('Erro ao executar diagnóstico');
    } finally {
      setIsDiagnosing(false);
    }
  };

  const handleClearCacheAndCookies = async () => {
    setShowClearCacheModal(true);
  };

  const confirmClearCache = async () => {
    setShowClearCacheModal(false);
    setIsClearingCache(true);
    try {
      // Limpar localStorage
      localStorage.clear();
      
      // Limpar sessionStorage
      sessionStorage.clear();
      
      // Limpar IndexedDB (se existir)
      if ('indexedDB' in window) {
        try {
          const databases = await indexedDB.databases();
          await Promise.all(
            databases.map(db => {
              if (db.name) {
                return new Promise((resolve, reject) => {
                  const deleteReq = indexedDB.deleteDatabase(db.name);
                  deleteReq.onsuccess = () => resolve(true);
                  deleteReq.onerror = () => reject(deleteReq.error);
                });
              }
            })
          );
        } catch (error) {
          console.warn('Erro ao limpar IndexedDB:', error);
        }
      }
      
      // Limpar Service Worker
      if ('serviceWorker' in navigator) {
        try {
          const registrations = await navigator.serviceWorker.getRegistrations();
          await Promise.all(
            registrations.map(registration => registration.unregister())
          );
        } catch (error) {
          console.warn('Erro ao limpar Service Workers:', error);
        }
      }
      
      // Limpar Cache API
      if ('caches' in window) {
        try {
          const cacheNames = await caches.keys();
          await Promise.all(
            cacheNames.map(cacheName => caches.delete(cacheName))
          );
        } catch (error) {
          console.warn('Erro ao limpar caches:', error);
        }
      }
      
      toast.success('Cache e cookies limpos com sucesso! Recarregando...');
      
      // Forçar reload da página para garantir atualização completa
      
      // Pequeno delay para garantir que o toast seja exibido
      setTimeout(() => {
        window.location.reload();
      }, 1500);
      
    } catch (error) {
      console.error('Erro ao limpar cache:', error);
      toast.error('Erro ao limpar cache e cookies');
    } finally {
      setIsClearingCache(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-2xl p-6 border border-gray-200/50 dark:border-gray-700/50 shadow-soft">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <button
              onClick={() => navigate(-1)}
              className="p-2 rounded-lg bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700/50 transition-all duration-200"
            >
              <ArrowLeft className="w-4 h-4" />
            </button>
            <div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">Editar Perfil</h1>
              <p className="text-gray-600 dark:text-gray-400">Gerencie suas informações pessoais e preferências</p>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Coluna Esquerda - Avatar */}
        <div className="space-y-6">
          {/* Avatar Section */}
          <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-2xl p-6 border border-gray-200/50 dark:border-gray-700/50 shadow-soft">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4 flex items-center">
              <Camera className="w-5 h-5 mr-2 text-blue-600 dark:text-blue-400" />
              Foto do Perfil
            </h3>
            
            <div className="text-center space-y-4">
              {/* Avatar Preview */}
              <div className="relative inline-block">
                <div className="w-32 h-32 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center overflow-hidden border-4 border-gray-100 dark:border-gray-700 shadow-lg">
                  {getCurrentAvatar() ? (
                    <img
                      src={getCurrentAvatar()}
                      alt="Avatar"
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <User className="w-16 h-16 text-gray-400 dark:text-gray-500" />
                  )}
                </div>
                
                {/* Upload Button */}
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="absolute bottom-0 right-0 w-10 h-10 bg-blue-600 dark:bg-primary-500 text-white rounded-full flex items-center justify-center hover:bg-blue-700 dark:hover:bg-primary-600 transition-colors shadow-lg"
                  title="Alterar foto"
                >
                  <Upload className="w-4 h-4" />
                </button>
              </div>

              {/* File Input Hidden */}
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                onChange={handleAvatarChange}
                className="hidden"
              />

              {/* Avatar Actions */}
              <div className="space-y-2">
                {avatarFile && (
                  <div className="space-y-2">
                    <button
                      onClick={handleAvatarUpload}
                      disabled={isUploadingAvatar}
                      className="w-full px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm font-medium"
                    >
                      {isUploadingAvatar ? 'Salvando...' : 'Salvar Nova Foto'}
                    </button>
                    
                    <button
                      onClick={() => {
                        setAvatarFile(null);
                        setAvatarPreview('');
                        if (fileInputRef.current) fileInputRef.current.value = '';
                      }}
                      className="w-full px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors text-sm font-medium"
                    >
                      Cancelar
                    </button>
                  </div>
                )}

                {usuario?.avatar && !avatarFile && (
                  <button
                    onClick={removeAvatar}
                    disabled={isUploadingAvatar}
                    className="w-full px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm font-medium flex items-center justify-center"
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Remover Foto
                  </button>
                )}
              </div>

              <p className="text-xs text-gray-500 dark:text-gray-400">
                Formatos aceitos: JPG, PNG, WebP<br />
                Tamanho máximo: 5MB
              </p>
            </div>
          </div>

          {/* User Info */}
          <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-2xl p-6 border border-gray-200/50 dark:border-gray-700/50 shadow-soft">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4 flex items-center">
              <User className="w-5 h-5 mr-2 text-blue-600 dark:text-blue-400" />
              Informações do Usuário
            </h3>
            
            <div className="space-y-3">
              <div className="flex items-center space-x-3 p-3 bg-gray-50 dark:bg-gray-900/50 rounded-lg">
                <Building2 className="w-4 h-4 text-blue-500 dark:text-blue-400" />
                <div>
                  <p className="text-sm font-medium text-gray-700 dark:text-gray-300">Nível</p>
                  <p className="text-sm text-gray-900 dark:text-gray-100">
                    {usuario?.nivel === 'dev'
                      ? 'Programador'
                      : usuario?.nivel === 'admin'
                      ? 'Operador'
                      : usuario?.nivel === 'usuario'
                      ? 'Gerente'
                      : usuario?.nivel}
                  </p>
                </div>
              </div>

              {usuario?.departamentos && usuario.departamentos.length > 0 && (
                <div className="flex items-center space-x-3 p-3 bg-gray-50 dark:bg-gray-900/50 rounded-lg">
                  <Building2 className="w-4 h-4 text-blue-500 dark:text-blue-400" />
                  <div>
                    <p className="text-sm font-medium text-gray-700 dark:text-gray-300">Departamentos</p>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {usuario.departamentos.map(dept => (
                        <span key={dept.id} className="px-2 py-1 bg-blue-100 dark:bg-blue-900/50 text-blue-800 dark:text-blue-400 text-xs rounded-full">
                          {dept.nome}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {usuario?.unidades && usuario.unidades.length > 0 && (
                <div className="flex items-center space-x-3 p-3 bg-gray-50 dark:bg-gray-900/50 rounded-lg">
                  <MapPin className="w-4 h-4 text-green-500 dark:text-green-400" />
                  <div>
                    <p className="text-sm font-medium text-gray-700 dark:text-gray-300">Unidades</p>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {usuario.unidades.map(unidade => (
                        <span key={unidade.id} className="px-2 py-1 bg-green-100 dark:bg-green-900/50 text-green-800 dark:text-green-400 text-xs rounded-full">
                          {unidade.nome}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              <div className="flex items-center space-x-3 p-3 bg-gray-50 dark:bg-gray-900/50 rounded-lg">
                <div className="w-4 h-4 rounded-full bg-green-500"></div>
                <div>
                  <p className="text-sm font-medium text-gray-700 dark:text-gray-300">Status</p>
                  <p className="text-sm text-gray-900 dark:text-gray-100">{usuario?.status === 1 ? 'Ativo' : 'Inativo'}</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Coluna Direita - Formulário */}
        <div className="lg:col-span-2">
          <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-2xl p-6 border border-gray-200/50 dark:border-gray-700/50 shadow-soft">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-6 flex items-center">
              <User className="w-5 h-5 mr-2 text-blue-600 dark:text-blue-400" />
              Dados Pessoais
            </h3>

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Nome */}
              <div>
                <label htmlFor="nome" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Nome Completo *
                </label>
                <input
                  type="text"
                  id="nome"
                  name="nome"
                  value={formData.nome}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:focus:ring-primary-400 focus:border-blue-500 transition-colors bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500"
                  placeholder="Digite seu nome completo"
                  required
                />
              </div>

              {/* Email */}
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Email *
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:focus:ring-primary-400 focus:border-blue-500 transition-colors bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500"
                  placeholder="Digite seu email"
                  required
                />
              </div>

              {/* Nova Senha */}
              <div className="space-y-4">
                <div>
                  <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                    Nova Senha
                  </label>
                  <div className="relative">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      id="password"
                      name="password"
                      value={formData.password}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 pr-12 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:focus:ring-primary-400 focus:border-blue-500 transition-colors bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500"
                      placeholder="Deixe em branco para manter a atual"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-400"
                    >
                      {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>

                {/* Requisitos de Senha */}
                {formData.password && (
                  <div className="bg-gray-50 dark:bg-gray-900/50 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
                    <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Requisitos da senha:</p>
                    <div className="space-y-2">
                      {(() => {
                        const requirements = validatePasswordRequirements(formData.password);
                        return (
                          <>
                            <div className="flex items-center gap-2">
                              {requirements.minLength ? (
                                <Check className="h-4 w-4 text-green-600 dark:text-green-400" />
                              ) : (
                                <XCircle className="h-4 w-4 text-gray-400 dark:text-gray-500" />
                              )}
                              <span className={`text-sm ${requirements.minLength ? 'text-green-700 dark:text-green-400' : 'text-gray-600 dark:text-gray-400'}`}>
                                Mínimo de 8 caracteres
                              </span>
                            </div>
                            <div className="flex items-center gap-2">
                              {requirements.maxLength ? (
                                <Check className="h-4 w-4 text-green-600 dark:text-green-400" />
                              ) : (
                                <XCircle className="h-4 w-4 text-gray-400 dark:text-gray-500" />
                              )}
                              <span className={`text-sm ${requirements.maxLength ? 'text-green-700 dark:text-green-400' : 'text-gray-600 dark:text-gray-400'}`}>
                                Máximo de 128 caracteres
                              </span>
                            </div>
                            <div className="flex items-center gap-2">
                              {requirements.hasLowercase ? (
                                <Check className="h-4 w-4 text-green-600 dark:text-green-400" />
                              ) : (
                                <XCircle className="h-4 w-4 text-gray-400 dark:text-gray-500" />
                              )}
                              <span className={`text-sm ${requirements.hasLowercase ? 'text-green-700 dark:text-green-400' : 'text-gray-600 dark:text-gray-400'}`}>
                                Pelo menos uma letra minúscula
                              </span>
                            </div>
                            <div className="flex items-center gap-2">
                              {requirements.hasUppercase ? (
                                <Check className="h-4 w-4 text-green-600 dark:text-green-400" />
                              ) : (
                                <XCircle className="h-4 w-4 text-gray-400 dark:text-gray-500" />
                              )}
                              <span className={`text-sm ${requirements.hasUppercase ? 'text-green-700 dark:text-green-400' : 'text-gray-600 dark:text-gray-400'}`}>
                                Pelo menos uma letra maiúscula
                              </span>
                            </div>
                            <div className="flex items-center gap-2">
                              {requirements.hasNumber ? (
                                <Check className="h-4 w-4 text-green-600 dark:text-green-400" />
                              ) : (
                                <XCircle className="h-4 w-4 text-gray-400 dark:text-gray-500" />
                              )}
                              <span className={`text-sm ${requirements.hasNumber ? 'text-green-700 dark:text-green-400' : 'text-gray-600 dark:text-gray-400'}`}>
                                Pelo menos um número
                              </span>
                            </div>
                            <div className="flex items-center gap-2">
                              {requirements.hasSpecial ? (
                                <Check className="h-4 w-4 text-green-600 dark:text-green-400" />
                              ) : (
                                <XCircle className="h-4 w-4 text-gray-400 dark:text-gray-500" />
                              )}
                              <span className={`text-sm ${requirements.hasSpecial ? 'text-green-700 dark:text-green-400' : 'text-gray-600 dark:text-gray-400'}`}>
                                Pelo menos um caractere especial (!@#$%^&*...)
                              </span>
                            </div>
                          </>
                        );
                      })()}
                    </div>
                  </div>
                )}

                {/* Confirmar Nova Senha */}
                {formData.password && (
                  <div>
                    <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Confirmar Nova Senha
                    </label>
                    <div className="relative">
                      <input
                        type={showConfirmPassword ? 'text' : 'password'}
                        id="confirmPassword"
                        name="confirmPassword"
                        value={formData.confirmPassword}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 pr-12 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:focus:ring-primary-400 focus:border-blue-500 transition-colors bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500"
                        placeholder="Confirme a nova senha"
                      />
                      <button
                        type="button"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-400"
                      >
                        {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                      </button>
                    </div>
                    {formData.confirmPassword && formData.password !== formData.confirmPassword && (
                      <p className="text-xs text-red-600 dark:text-red-400 mt-1">As senhas não coincidem</p>
                    )}
                    {formData.confirmPassword && formData.password === formData.confirmPassword && (
                      <p className="text-xs text-green-600 dark:text-green-400 mt-1">As senhas coincidem</p>
                    )}
                  </div>
                )}
              </div>

              {/* Preferências */}
              <div className="border-t border-gray-200 dark:border-gray-700 pt-6">
                <h4 className="text-md font-medium text-gray-900 dark:text-gray-100 mb-4">Preferências</h4>
                
                <div className="flex items-center space-x-3">
                  <input
                    type="checkbox"
                    id="exibir_fechados"
                    name="exibir_fechados"
                    checked={formData.exibir_fechados === 1}
                    onChange={handleInputChange}
                    className="w-4 h-4 text-blue-600 dark:text-primary-500 focus:ring-blue-500 dark:focus:ring-primary-400 border-gray-300 dark:border-gray-600 rounded"
                  />
                  <label htmlFor="exibir_fechados" className="text-sm text-gray-700 dark:text-gray-300">
                    Exibir tickets fechados por padrão
                  </label>
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Quando ativado, os tickets fechados serão exibidos automaticamente nas listagens
                </p>
              </div>

              {/* Botões */}
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center space-y-3 sm:space-y-0 sm:space-x-4 pt-6 border-t border-gray-200 dark:border-gray-700">
                <button
                  type="button"
                  onClick={() => navigate(-1)}
                  className="px-6 py-3 text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 dark:focus:ring-primary-400 transition-colors font-medium"
                >
                  Cancelar
                </button>
                
                <button
                  type="submit"
                  disabled={isSaving}
                  className="flex-1 sm:flex-none px-6 py-3 bg-blue-600 dark:bg-primary-500 text-white rounded-lg hover:bg-blue-700 dark:hover:bg-primary-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 dark:focus:ring-primary-400 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium"
                >
                  {isSaving ? (
                    <div className="flex items-center justify-center">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Salvando...
                    </div>
                  ) : (
                    'Salvar Alterações'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
      {/* Notificações e Sistema */}
      <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-2xl p-6 border border-gray-200/50 dark:border-gray-700/50 shadow-soft">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4 flex items-center">
              <Settings className="w-5 h-5 mr-2 text-blue-600 dark:text-blue-400" />
              Notificações e Sistema
            </h3>
            
            <div className="space-y-6">
              {/* Layout Horizontal - Duas Colunas */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {/* Seção de Notificações Push */}
                <div className="space-y-4">
                  <div className="flex items-center space-x-2 mb-4">
                    <div className="p-2 bg-blue-100 dark:bg-blue-900/50 rounded-lg">
                      <Bell className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                    </div>
                    <h4 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                      Notificações Push
                    </h4>
                  </div>
                  
                  {/* Status das Notificações */}
                  <div className={`p-4 rounded-xl border-2 ${
                    !isSupported 
                      ? 'bg-red-50 dark:bg-red-900/30 border-red-200 dark:border-red-800 text-red-800 dark:text-red-300'
                      : permission === 'granted' && hasActiveSubscription
                      ? 'bg-green-50 dark:bg-green-900/30 border-green-200 dark:border-green-800 text-green-800 dark:text-green-300'
                      : permission === 'denied'
                      ? 'bg-red-50 dark:bg-red-900/30 border-red-200 dark:border-red-800 text-red-800 dark:text-red-300'
                      : 'bg-yellow-50 dark:bg-yellow-900/30 border-yellow-200 dark:border-yellow-800 text-yellow-800 dark:text-yellow-300'
                  }`}>
                    <div className="flex items-start space-x-3">
                      {!isSupported ? (
                        <AlertTriangle className="w-5 h-5 text-red-600 mt-0.5" />
                      ) : permission === 'granted' && hasActiveSubscription ? (
                        <CheckCircle className="w-5 h-5 text-green-600 mt-0.5" />
                      ) : (
                        <AlertTriangle className="w-5 h-5 text-yellow-600 mt-0.5" />
                      )}
                      <div className="flex-1">
                        <p className="font-semibold text-sm mb-1">
                          {!isSupported 
                            ? 'Não Suportado'
                            : permission === 'granted' && hasActiveSubscription
                            ? 'Ativadas e Funcionando'
                            : permission === 'denied'
                            ? 'Permissão Negada'
                            : 'Precisa Configurar'
                          }
                        </p>
                        <p className="text-xs leading-relaxed">
                          {!isSupported 
                            ? 'Este navegador não suporta notificações push'
                            : permission === 'granted' && hasActiveSubscription
                            ? 'Você receberá notificações importantes'
                            : permission === 'denied'
                            ? 'Reative nas configurações do navegador'
                            : 'Configure para receber alertas importantes'
                          }
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Ações das Notificações */}
                  <div className="space-y-3">
                    {needsAttention && (
                      <button
                        onClick={() => navigate('/notificacoes/configurar')}
                        className="w-full flex items-center justify-center px-4 py-3 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all duration-200 shadow-sm"
                      >
                        <Bell className="w-4 h-4 mr-2" />
                        Configurar Notificações
                      </button>
                    )}
                    
                    {permission === 'granted' && hasActiveSubscription && (
                      <div className="grid grid-cols-1 gap-3">
                        <button
                          onClick={handleTestPushNotification}
                          disabled={isTestingNotification}
                          className="w-full flex items-center justify-center px-4 py-3 bg-green-600 text-white font-medium rounded-lg hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
                        >
                          <TestTube className="w-4 h-4 mr-2" />
                          {isTestingNotification ? 'Testando...' : 'Testar Notificação'}
                        </button>
                        
                        <button
                          onClick={handleDiagnosticoPush}
                          disabled={isDiagnosing}
                          className="w-full flex items-center justify-center px-4 py-3 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
                        >
                          <Settings className="w-4 h-4 mr-2" />
                          {isDiagnosing ? 'Diagnosticando...' : 'Diagnóstico Push'}
                        </button>
                      </div>
                    )}
                  </div>
                </div>

                {/* Seção de Limpeza de Cache */}
                <div className="space-y-4">
                    <div className="flex items-center space-x-2 mb-4">
                    <div className="p-2 bg-amber-100 dark:bg-amber-900/50 rounded-lg">
                      <Database className="w-5 h-5 text-amber-600 dark:text-amber-400" />
                    </div>
                    <h4 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                      Limpeza de Cache
                    </h4>
                  </div>
                  
                  {/* Status do Cache */}
                  <div className="p-4 rounded-xl border-2 bg-amber-50 dark:bg-amber-900/30 border-amber-200 dark:border-amber-800 text-amber-800 dark:text-amber-300">
                    <div className="flex items-start space-x-3">
                      <AlertTriangle className="w-5 h-5 text-amber-600 dark:text-amber-400 mt-0.5" />
                      <div className="flex-1">
                        <p className="font-semibold text-sm mb-1">
                          Dados Locais
                        </p>
                        <p className="text-xs leading-relaxed">
                          Use esta função para resolver problemas de carregamento ou dados desatualizados
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Ação de Limpeza */}
                  <button
                    onClick={handleClearCacheAndCookies}
                    disabled={isClearingCache}
                    className="w-full flex items-center justify-center px-4 py-3 bg-amber-600 text-white font-medium rounded-lg hover:bg-amber-700 focus:outline-none focus:ring-2 focus:ring-amber-500 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
                  >
                    <RefreshCw className={`w-4 h-4 mr-2 ${isClearingCache ? 'animate-spin' : ''}`} />
                    {isClearingCache ? 'Limpando...' : 'Limpar Cache e Cookies'}
                  </button>
                </div>
              </div>
            </div>

            {/* Informações Gerais - Fora do space-y-6, ocupando toda a largura */}
            <div className="mt-6 bg-gradient-to-r from-gray-50 dark:from-gray-900/50 to-blue-50 dark:to-blue-900/30 rounded-xl p-4 border border-gray-200 dark:border-gray-700">
              <div className="flex items-start space-x-3">
                <div className="p-2 bg-blue-100 dark:bg-blue-900/50 rounded-lg mt-0.5">
                  <Settings className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                </div>
                <div className="flex-1">
                  <h5 className="font-semibold text-sm text-gray-900 dark:text-gray-100 mb-2">Informações do Sistema</h5>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs text-gray-600 dark:text-gray-400">
                    <div>
                      <strong className="text-blue-700 dark:text-blue-400">Notificações:</strong> Receba alertas importantes mesmo quando não estiver usando o sistema.
                    </div>
                    <div>
                      <strong className="text-amber-700 dark:text-amber-400">Cache:</strong> A limpeza remove todos os dados locais e força uma atualização completa.
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

      {/* Modal de Confirmação para Limpeza de Cache */}
      {showClearCacheModal && (
        <Modal
          isOpen={showClearCacheModal}
          onClose={() => setShowClearCacheModal(false)}
          title={
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-amber-100 dark:bg-amber-900/50 rounded-lg">
                <AlertTriangle className="w-6 h-6 text-amber-600 dark:text-amber-400" />
              </div>
              <div>
                <div className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                  Confirmar Limpeza de Cache
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Esta ação não pode ser desfeita
                </p>
              </div>
            </div>
          }
          maxWidth="md"
        >

            {/* Conteúdo do Modal */}
            <div className="p-6">
              <div className="space-y-4">
                <div className="bg-amber-50 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-800 rounded-lg p-4">
                  <h4 className="font-medium text-amber-800 dark:text-amber-300 mb-2">
                    O que será removido:
                  </h4>
                  <ul className="text-sm text-amber-700 dark:text-amber-400 space-y-1">
                    <li className="flex items-center space-x-2">
                      <div className="w-1.5 h-1.5 bg-amber-500 rounded-full"></div>
                      <span>Todos os dados armazenados localmente</span>
                    </li>
                    <li className="flex items-center space-x-2">
                      <div className="w-1.5 h-1.5 bg-amber-500 rounded-full"></div>
                      <span>Cookies do sistema</span>
                    </li>
                    <li className="flex items-center space-x-2">
                      <div className="w-1.5 h-1.5 bg-amber-500 rounded-full"></div>
                      <span>Cache do navegador</span>
                    </li>
                    <li className="flex items-center space-x-2">
                      <div className="w-1.5 h-1.5 bg-amber-500 rounded-full"></div>
                      <span>Service Workers</span>
                    </li>
                  </ul>
                </div>

                <div className="bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                  <div className="flex items-start space-x-2">
                    <div className="p-1 bg-blue-100 dark:bg-blue-900/50 rounded">
                      <RefreshCw className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-blue-800 dark:text-blue-300 mb-1">
                        Após a limpeza:
                      </p>
                      <p className="text-sm text-blue-700 dark:text-blue-400">
                        Você será redirecionado automaticamente para a tela de login e precisará fazer login novamente.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Footer do Modal */}
            <div className="p-6 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50 rounded-b-2xl">
              <div className="flex flex-col sm:flex-row space-y-3 sm:space-y-0 sm:space-x-3">
                <button
                  onClick={() => setShowClearCacheModal(false)}
                  className="flex-1 px-4 py-3 text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/50 focus:outline-none focus:ring-2 focus:ring-gray-500 dark:focus:ring-gray-400 transition-colors font-medium"
                >
                  Cancelar
                </button>
                <button
                  onClick={confirmClearCache}
                  disabled={isClearingCache}
                  className="flex-1 px-4 py-3 bg-amber-600 dark:bg-amber-500 text-white rounded-lg hover:bg-amber-700 dark:hover:bg-amber-600 focus:outline-none focus:ring-2 focus:ring-amber-500 dark:focus:ring-amber-400 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium flex items-center justify-center"
                >
                  {isClearingCache ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Limpando...
                    </>
                  ) : (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2" />
                      Confirmar
                    </>
                  )}
                </button>
              </div>
            </div>
        </Modal>
      )}
    </div>
  );
}
